import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';

interface Report {
  id: number;
  type: 'ad_report' | 'user_report';
  subject: string;
  reason: string;
  details?: string;
  status: string;
  created_at: string;
}

interface Meta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const PER_PAGE = 10;

const ReportsPage: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userId = params.get('user_id');
  const isAr = params.get('language') === 'ar';

  const [reports, setReports] = useState<Report[]>([]);
  const [meta, setMeta]       = useState<Meta>({ total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);

  useEffect(() => {
    document.documentElement.dir = isAr ? 'rtl' : 'ltr';
  }, [isAr]);

  const fetchReports = useCallback((p: number) => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${env.apiUrl}/getUserReports`, { params: { user_id: userId, page: p, per_page: PER_PAGE } })
      .then((res) => {
        if (res.data.status) {
          setReports(res.data.reports);
          setMeta(res.data.meta);
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { fetchReports(page); }, [page, fetchReports]);

  const goTo = (p: number) => {
    if (p < 1 || p > meta.last_page) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = (): (number | '…')[] => {
    const { current_page: cur, last_page: last } = meta;
    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (cur > 3) pages.push('…');
    for (let i = Math.max(2, cur - 1); i <= Math.min(last - 1, cur + 1); i++) pages.push(i);
    if (cur < last - 2) pages.push('…');
    pages.push(last);
    return pages;
  };

  return (
    <div style={{ ...styles.container, direction: isAr ? 'rtl' : 'ltr' }}>
      <h1 style={styles.pageTitle}>
        {isAr ? 'بلاغاتك المقدمة' : 'Your Submitted Reports'}
      </h1>

      <p style={styles.introText}>
        {isAr
          ? 'فيما يلي البلاغات التي قدمتها. يراجع فريقنا كل حالة بعناية وفقًا لسياسات المنصة.'
          : 'Below are the reports you have submitted. Our team reviews each case carefully according to platform policies.'}
      </p>

      <div style={styles.noteBox}>
        <p style={styles.noteTitle}>{isAr ? 'ملاحظة مهمة' : 'Important Note'}</p>
        <p style={styles.noteText}>
          {isAr
            ? 'تُراجع جميع البلاغات بسرية تامة. تقديم البلاغ لا يضمن اتخاذ إجراء فوري، إذ يتم تقييم كل حالة بناءً على الأدلة والمعايير المحددة.'
            : 'All reports are reviewed confidentially. Submitting a report does not guarantee immediate action, as each case is evaluated based on evidence and policy standards.'}
        </p>
      </div>

      <div style={styles.sectionHeader}>
        <p style={styles.sectionLabel}>{isAr ? 'تفاصيل البلاغ' : 'Report Details'}</p>
      </div>
      <div style={styles.divider} />

      {loading ? (
        <p style={styles.emptyText}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</p>
      ) : reports.length === 0 ? (
        <p style={styles.emptyText}>{isAr ? 'لا توجد بلاغات مقدمة.' : 'No reports submitted.'}</p>
      ) : (
        <>
          {reports.map((r, i) => (
            <div key={`${r.type}-${r.id}`}>
              <div style={styles.reportRow}>
                <div style={styles.rowTop} />
                <p style={styles.reportSubject}>
                  {isAr ? 'الحساب / الإعلان المبلَّغ عنه: ' : 'Reported Account / Ad: '}{r.subject}
                </p>
                <p style={styles.reportText}>
                  <span>{isAr ? 'السبب: ' : 'Reason: '}</span>{r.reason}
                </p>
                <p style={styles.reportMeta}>
                  {isAr ? 'تاريخ التقديم: ' : 'Date Submitted: '}{r.created_at}
                </p>
              </div>
              {i < reports.length - 1 && <div style={styles.rowDivider} />}
            </div>
          ))}

          {meta.last_page > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.35 : 1 }}
                onClick={() => goTo(page - 1)}
                disabled={page === 1}
              >
                {isAr ? '›' : '‹'}
              </button>

              {pageNumbers().map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-${i}`} style={styles.ellipsis}>…</span>
                ) : (
                  <button
                    key={p}
                    style={{ ...styles.pageBtn, ...(p === page ? styles.pageBtnActive : {}) }}
                    onClick={() => goTo(p as number)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                style={{ ...styles.pageBtn, opacity: page === meta.last_page ? 0.35 : 1 }}
                onClick={() => goTo(page + 1)}
                disabled={page === meta.last_page}
              >
                {isAr ? '‹' : '›'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'Segoe UI', Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '100vh',
    padding: '24px 20px',
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111',
    textAlign: 'center',
    marginBottom: 16,
    marginTop: 0,
  },
  introText: {
    fontSize: 12,
    fontWeight: 400,
    color: '#333333',
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: 24,
  },
  noteBox: {
    textAlign: 'center',
    marginBottom: 28,
  },
  noteTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#111',
    marginBottom: 8,
    marginTop: 0,
  },
  noteText: {
    fontSize: 12,
    color: '#333333',
    lineHeight: 1.7,
    margin: 0,
    textAlign: 'center',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#333333',
    margin: 0,
  },
  divider: {
    borderBottom: '2px solid #111',
    marginTop: 6,
    marginBottom: 14,
  },
  reportRow: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  reportSubject: {
    fontSize: 12,
    fontWeight: 400,
    color: '#333333',
    margin: '0 0 6px',
  },
  reportText: {
    fontSize: 12,
    fontWeight: 400,
    color: '#333333',
    margin: '3px 0',
    lineHeight: 1.6,
  },
  reportMeta: {
    fontSize: 12,
    fontWeight: 400,
    color: '#333333',
    margin: '6px 0 0',
  },
  rowDivider: {
    borderBottom: '1px solid #eee',
  },
  emptyText: {
    fontSize: 14,
    color: '#666',
    marginTop: 12,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginTop: 28,
    paddingBottom: 16,
  },
  pageBtn: {
    minWidth: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
  },
  pageBtnActive: {
    background: '#111',
    color: '#fff',
    borderColor: '#111',
    fontWeight: 700,
  },
  ellipsis: {
    fontSize: 14,
    color: '#999',
    padding: '0 2px',
  },
};

export default ReportsPage;
